import { Component, EventEmitter, Input, Output, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SelectionItem } from '../../select/select.component';

export type PagingEvent = { from: number; to: number; pageNumber: number; itemsPerPage: number };

export const DefaultPageSize = 50;

@Component({
    selector: 'app-paging',
    templateUrl: './paging.component.html',
    styleUrls: ['./paging.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})                       
export class PagingComponent implements OnInit {
  constructor() {
    this.displayCountSelection = [new SelectionItem('20', 20), new SelectionItem('50', 50), new SelectionItem('100', 100)];

    this.pageIndex = 0;
  }

  _totalCount: number;
  @Input() compact: boolean = false;

  @Input() set pageNumber(value: number) {
    if (!value || value < 1) return;

    const nextPageIndex = value - 1;
    if (nextPageIndex === this._pageIndex) return;

    this._pageIndex = nextPageIndex;
    this.setPageSelection();
  }

  @Input() set itemsPerPage(value: number) {
    if (!value || value === this._displayCount) return;

    this._displayCount = value;
    this.setPageSelection();
  }

  @Input() set totalCount(count: number) {
    let reset = count != this._totalCount;
    this._totalCount = count;
    this.setPageSelection();
    if (reset) {
      this.pageIndex = 0;
    }
  }

  get totalCount(): number {
    return this._totalCount;
  }
  @Output() onPageChange = new EventEmitter<PagingEvent>();

  get displayFirstLast(): boolean {
    return this.lastPageIndex >= 10;
  }

  get displayPaging(): boolean {
    return this.lastPageIndex > 0;
  }

  get rangeStart(): number {
    if (!this.totalCount) return 0;
    return this.pageIndex * this.displayCount + 1;
  }

  get rangeEnd(): number {
    if (!this.totalCount) return 0;
    return Math.min((this.pageIndex + 1) * this.displayCount, this.totalCount);
  }

  nextPage() {
    if (this.pageIndex < this.lastPageIndex) this.pageIndex++;
  }

  previousPage() {
    if (this.pageIndex > 0) this.pageIndex--;
  }

  displayCountSelection: SelectionItem[];
  currentPageItems: any[] = [];

  private _displayCount = DefaultPageSize;
  private _pageIndex = 0;

  get displayCount(): number {
    return this._displayCount;
  }

  set displayCount(value: number) {
    if (!this.totalCount) return;

    if (value === this._displayCount) return;

    this._displayCount = value;
    this.pageIndex = 0;
    this.onPageChange.emit({ from: 0, to: value - 1, itemsPerPage: this._displayCount, pageNumber: this.pageIndex + 1 });
  }

  get pageIndex(): number {
    return this._pageIndex;
  }

  set pageIndex(value: number) {
    if (!this.totalCount) return;

    const startIndex = value * this.displayCount;
    const endIndex = Math.min(startIndex + this.displayCount, this.totalCount);

    if (startIndex >= Math.max(this.totalCount, 1)) return;

    this._pageIndex = value;
    this.setPageSelection();
    this.onPageChange.emit({ from: startIndex, to: endIndex, itemsPerPage: this._displayCount, pageNumber: this.pageIndex + 1 });
  }

  pageSelection: SelectionItem[];

  get lastPageIndex(): number {
    if (!this.totalCount || !this.displayCount) return 0;
    let lastPageIndex = Math.floor(this.totalCount / this.displayCount);

    if (lastPageIndex * this.displayCount == this.totalCount) {
      lastPageIndex--;
    }
    return lastPageIndex;
  }

  ngOnInit() {}

  setPageSelection() {
    const lastPossiblePage = Math.min(this.lastPageIndex, Math.max(this.pageIndex + 5, 9));
    const firstPossiblePage = Math.max(0, lastPossiblePage - 9);
    const selection = [];
    for (let page = firstPossiblePage; page <= lastPossiblePage; page++) selection.push(new SelectionItem((page + 1).toString(), page));
    this.pageSelection = selection;
  }
}
